import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import adminService from '../../../services/adminService';

const FunctionsManagement = () => {
  const [functions, setFunctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingFunction, setEditingFunction] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadFunctions();
  }, []);

  const loadFunctions = async () => {
    try {
      setLoading(true);
      const data = await adminService?.getAllFunctions();
      setFunctions(data || []);
    } catch (error) {
      console.error('Error loading functions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (func = null) => {
    if (func) {
      setEditingFunction(func);
      setFormData({
        name: func?.name,
        description: func?.description || '',
        is_active: func?.is_active
      });
    } else {
      setEditingFunction(null);
      setFormData({
        name: '',
        description: '',
        is_active: true
      });
    }
    setErrors({});
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingFunction(null);
    setFormData({ name: '', description: '', is_active: true });
    setErrors({});
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.name?.trim()) {
      newErrors.name = 'Naam is verplicht';
    }
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;

    try {
      if (editingFunction) {
        await adminService?.updateFunction(editingFunction?.id, formData);
      } else {
        await adminService?.createFunction(formData);
      }
      await loadFunctions();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving function:', error);
      setErrors({ submit: error?.message });
    }
  };

  const handleDelete = async (functionId) => {
    if (!window.confirm('Weet je zeker dat je deze functie wilt verwijderen?')) return;

    try {
      await adminService?.deleteFunction(functionId);
      await loadFunctions();
    } catch (error) {
      console.error('Error deleting function:', error);
      alert('Fout bij verwijderen: ' + error?.message);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Functies Beheer</h2>
          <p className="text-sm text-muted-foreground">Beheer werktypen en functies</p>
        </div>
        <Button onClick={() => handleOpenModal()} iconName="Plus">
          Functie Toevoegen
        </Button>
      </div>
      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Laden...</div>
      ) : (
        <div 
          className="bg-white border-2 border-slate-200 rounded-lg overflow-hidden"
          style={{
            boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
          }}
        >
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Naam</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Beschrijving</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Status</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-foreground">Acties</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {functions?.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-4 py-8 text-center text-muted-foreground">
                    Geen functies gevonden
                  </td>
                </tr>
              ) : (
                functions?.map((func) => (
                  <tr key={func?.id} className="hover:bg-muted/30">
                    <td className="px-4 py-3 text-sm text-foreground font-medium">{func?.name}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">{func?.description || '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        func?.is_active ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'
                      }`}>
                        {func?.is_active ? 'Actief' : 'Inactief'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Edit"
                        onClick={() => handleOpenModal(func)}
                      >
                        Bewerken
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Trash2"
                        onClick={() => handleDelete(func?.id)}
                      >
                        Verwijderen
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
      {isModalOpen && (
        <div className="fixed inset-0 z-1030 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={handleCloseModal} />
          <div 
            className="relative bg-white border-2 border-slate-200 rounded-lg w-full max-w-md"
            style={{
              boxShadow: '0 20px 60px rgba(0,0,0,0.2), 0 10px 30px rgba(0,0,0,0.12)'
            }}
          >
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">
                {editingFunction ? 'Functie Bewerken' : 'Nieuwe Functie'}
              </h3>
              <Button variant="ghost" size="icon" onClick={handleCloseModal}>
                <Icon name="X" size={20} />
              </Button>
            </div>

            <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
              <Input
                label="Naam"
                type="text"
                placeholder="Bijv. Zonnepanelen"
                value={formData?.name}
                onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
                error={errors?.name}
                required
              />

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Beschrijving
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  rows="3"
                  placeholder="Optionele beschrijving"
                  value={formData?.description}
                  onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
                />
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="is_active"
                  checked={formData?.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <label htmlFor="is_active" className="text-sm text-foreground cursor-pointer">
                  Actief
                </label>
              </div>

              {errors?.submit && (
                <div className="text-sm text-error">{errors?.submit}</div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={handleCloseModal}>
                  Annuleren
                </Button>
                <Button type="submit">
                  {editingFunction ? 'Opslaan' : 'Toevoegen'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FunctionsManagement;